import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useSport } from "@/context/SportContext";
import { ArrowLeft, Save, Bookmark, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SKILL_LEVELS = ["beginner", "improver", "intermediate", "advanced", "elite"];

// Past sessions with players, and completed sessions, are read-only.
export const editLockReason = (o) => {
  if (!o || o.is_package) return "";
  if (o.status === "completed") return "This session is completed, so it's read-only.";
  const enrolled = (o.enrolled_student_ids || []).length || o.enrolled_count || 0;
  if (o.start && new Date(o.start) < new Date() && enrolled > 0) return "This session is in the past and has players enrolled, so it can't be edited.";
  return "";
};

export default function OfferingNew() {
  const { sport, skillsBySport } = useSport();
  const nav = useNavigate();
  // Iter87 — if the coach lands here with ?edit_id=… we swap the whole page
  // into edit mode: hydrate the form from the existing offering, PUT on save
  // instead of POST, and hide fields the backend refuses to change (sport,
  // session_type, is_assessment stay locked because bookings depend on them).
  const editId = useMemo(() => new URLSearchParams(window.location.search).get("edit_id") || null, []);
  const copyId = useMemo(() => new URLSearchParams(window.location.search).get("copy_id") || null, []);
  const isEdit = !!editId;
  const [locked, setLocked] = useState("");
  const today = new Date();
  const defaultDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    sport, session_type: "group", skill_level: "intermediate", skill_focus: [],
    venue: "", title: "", date: defaultDate, time: "18:00",
    duration_min: 60, max_spaces: 6, price_per_player: 12, notes: "",
    save_as_template: false,
    is_assessment: false,
    shared_with_team: false,
    region_ids: [],  // iter93 — multi-region: an offering can belong to N overlapping regions
    reschedule_cutoff_hours: "",  // "" = fall back to coach default
    // Iter143 — Min players + deadline. Coach-configurable per offering.
    min_players: "",           // "" = no minimum enforced (backwards compatible)
    min_deadline_at: "",       // datetime-local — coach's manual cut-off
    // Iter94 — Package Store + credit acceptance
    is_package: false,       // true → this offering is a bundle sold as N credits (no date/time)
    session_count: 5,        // only used when is_package === true
    expiry_months: 6,        // credits expire N months after purchase (user choice 1b)
    accept_credits: false,   // single-session only — allow students to spend a pre-paid credit
    allow_pay_when_credit_available: true,  // iter112 — when credits accepted, still show a Pay-cash fallback link (default on)
    allow_promo_codes: true, // iter97 — untick to lock this offering out of site-wide promo codes
  });
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const [myTeams, setMyTeams] = useState([]);
  const [teamConflict, setTeamConflict] = useState(null);
  const [myRegions, setMyRegions] = useState([]);  // populated from /auth/me + /regions
  // Iter144 — Coach's assigned sports drive which "SPORT" buttons are
  // enabled on this form. Admins keep both options. Non-admin coaches only
  // see the sport(s) in their `coach_sports` array; the other tile is
  // greyed out with a hover tooltip explaining why.
  const [mySports, setMySports] = useState(null); // null while loading, [] for admin (no restriction)
  // Iter93c — Region tag templates. A coach saves their two-or-three-region
  // combos ("Manchester + Cheshire") so the next multi-region publish is one
  // tap. Templates live under /me/region-templates (personal only, never shared).
  const [templates, setTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const skills = skillsBySport[form.sport] || [];

  // Lazy-load the coach's teams so the share toggle only appears when there's at
  // least one team to share with. Avoids polluting the form with a useless tick-box.
  useEffect(() => {
    api.get("/teams").then((r) => setMyTeams(r.data || [])).catch(() => setMyTeams([]));
    // Resolve the coach's approved region IDs into full region objects for the picker.
    // Admins bypass the approval gate so we show them the full active region list.
    (async () => {
      try {
        const [me, allRegions] = await Promise.all([api.get("/auth/me"), api.get("/regions?active=true")]);
        const isAdmin = me.data?.role === "admin"
          || (me.data?.permissions || []).includes("admin")
          || (me.data?.permissions || []).includes("super_admin");
        const ids = new Set(me.data?.regions || []);
        const mine = isAdmin
          ? (allRegions.data || [])  // admin sees all
          : (allRegions.data || []).filter((r) => ids.has(r.id));
        setMyRegions(mine);
        // Iter144 — Cache the coach's assigned sports. Admins get an empty
        // array which the picker treats as "no restriction". If the coach
        // is assigned only one sport and their form currently points at the
        // other, snap the selection over so the submit isn't stuck disabled.
        if (isAdmin) {
          setMySports([]);
        } else {
          const assigned = Array.isArray(me.data?.coach_sports) ? me.data.coach_sports : [];
          setMySports(assigned);
          if (assigned.length > 0 && !assigned.includes(form.sport)) {
            setForm((f) => ({ ...f, sport: assigned[0] }));
          }
        }
        // Default to first region if not yet set
        if (mine.length > 0 && !form.region_ids?.length) {
          setForm((f) => ({ ...f, region_ids: [mine[0].id] }));
        }
      } catch (_e) { setMyRegions([]); setMySports([]); }
    })();
    // Templates load in parallel — silent failure keeps the picker usable
    // even if the endpoint is down.
    api.get("/me/region-templates")
      .then((r) => setTemplates(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTemplates([]));
  }, []);

  // Iter87 — hydrate form from an existing offering when editing.
  useEffect(() => {
    const srcId = editId || copyId;
    if (!srcId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/offerings/${srcId}`);
        if (cancelled || !data) return;
        if (editId) setLocked(editLockReason(data));
        // Iter94 — packages have no `start`, so guard the date/time formatter.
        const isPkg = !!data.is_package;
        const dt = isPkg ? new Date() : new Date(data.start);
        const pad = (n) => String(n).padStart(2, "0");
        setForm((f) => ({
          ...f,
          sport: data.sport || f.sport,
          session_type: data.session_type || f.session_type,
          skill_level: data.skill_level || f.skill_level,
          skill_focus: Array.isArray(data.skill_focus) ? data.skill_focus : [],
          venue: data.venue || "",
          title: data.title || "",
          date: copyId ? f.date : `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
          time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
          duration_min: data.duration_min ?? 60,
          max_spaces: data.max_spaces ?? 6,
          price_per_player: data.price_per_player ?? 0,
          notes: data.notes || "",
          is_assessment: !!data.is_assessment,
          shared_with_team: !!data.shared_with_team,
          region_ids: Array.isArray(data.region_ids) && data.region_ids.length
            ? data.region_ids
            : (data.region_id ? [data.region_id] : []),
          reschedule_cutoff_hours: data.reschedule_cutoff_hours ?? "",
          // Iter94 — package + credit flags rehydrated from server
          is_package: isPkg,
          session_count: data.session_count ?? 5,
          expiry_months: data.expiry_months ?? 6,
          accept_credits: !!data.accept_credits,
          allow_pay_when_credit_available: data.allow_pay_when_credit_available !== false,  // iter112 — default true
          allow_promo_codes: data.allow_promo_codes !== false,  // iter97 — default true if server didn't set it
        }));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load offering to edit");
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editId, copyId]);

  // Run a team conflict probe every time the date/time/share toggle changes so the
  // coach sees a warning BEFORE publishing if any team-mate already has a session
  // overlapping. Debounced to one call per 600ms so we don't hammer the backend.
  useEffect(() => {
    if (!form.shared_with_team || !form.date || !form.time) {
      setTeamConflict(null);
      return;
    }
    const start = `${form.date}T${form.time}:00`;
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.post("/bookings/check-conflict", {
          start, duration_min: Number(form.duration_min),
          include_team: true,
        });
        setTeamConflict(data);
      } catch (_e) { setTeamConflict(null); }
    }, 600);
    return () => clearTimeout(handle);
  }, [form.shared_with_team, form.date, form.time, form.duration_min]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSkill = (s) => {
    setForm((f) => ({
      ...f,
      skill_focus: f.skill_focus.includes(s) ? f.skill_focus.filter((x) => x !== s) : [...f.skill_focus, s],
    }));
  };

  // Iter93c — Region templates: apply / save / delete. Placed AFTER `update`
  // and `form` are in scope so the callbacks close over the current state.
  const applyTemplate = (t) => {
    const allowed = new Set((myRegions || []).map((r) => r.id));
    const useable = (t.region_ids || []).filter((rid) => allowed.has(rid));
    if (useable.length === 0) {
      toast.error(`"${t.name}" references regions you no longer coach — delete it or ask an admin.`);
      return;
    }
    update("region_ids", useable);
    toast.success(`Applied "${t.name}"`);
  };

  const saveTemplate = async () => {
    const chosen = form.region_ids || [];
    if (chosen.length < 2) {
      toast.error("Pick at least two regions before saving a template.");
      return;
    }
    // Native browser prompt keeps this a one-tap experience — no modal library needed.
    const name = window.prompt("Name this region combo (e.g. Manchester + Cheshire):", "");
    if (name === null) return;
    const trimmed = (name || "").trim();
    if (!trimmed) { toast.error("Template name can't be blank."); return; }
    setSavingTemplate(true);
    try {
      const { data } = await api.post("/me/region-templates", { name: trimmed, region_ids: chosen });
      setTemplates((prev) => [data, ...prev]);
      toast.success(`Saved "${trimmed}"`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save template");
    } finally { setSavingTemplate(false); }
  };

  const deleteTemplate = async (t) => {
    // Native confirm keeps parity with other destructive actions on this page.
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/me/region-templates/${t.id}`);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Template deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete template");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.is_package && !form.venue.trim()) { toast.error("Venue required"); return; }
    setSaving(true);
    try {
      // Iter94 — Packages have no calendar footprint: skip the start-time
      // composition entirely so the backend can null the start/duration.
      const start = form.is_package ? null : `${form.date}T${form.time}:00`;
      if (isEdit) {
        // PUT — only fields the backend accepts on edit. Sport, session_type,
        // is_assessment, and is_package itself are intentionally NOT sent
        // (immutable on an existing offering with enrolments). Region tags,
        // credit-acceptance, session_count and expiry_months CAN be edited.
        await api.put(`/offerings/${editId}`, {
          title: form.title || null,
          skill_level: form.skill_level,
          skill_focus: form.skill_focus,
          venue: form.venue,
          ...(form.is_package
            ? { session_count: Number(form.session_count), expiry_months: Number(form.expiry_months), allow_promo_codes: !!form.allow_promo_codes }
            : { start, duration_min: Number(form.duration_min), max_spaces: Number(form.max_spaces),
                accept_credits: !!form.accept_credits,
                allow_pay_when_credit_available: !!form.allow_pay_when_credit_available,
                // Iter143 — min-players safety net on edit
                min_players: form.min_players === "" || form.min_players === null ? null : Number(form.min_players),
                min_deadline_at: form.min_deadline_at || null }),
          price_per_player: Number(form.price_per_player),
          notes: form.notes,
          region_ids: form.region_ids || [],
          reschedule_cutoff_hours: form.reschedule_cutoff_hours === "" || form.reschedule_cutoff_hours === null
            ? null
            : Math.max(0, Math.min(720, Number(form.reschedule_cutoff_hours))),
        });
        toast.success(form.is_package ? "Package updated" : "Offering updated");
      } else {
        await api.post("/offerings", {
          sport: form.sport, session_type: form.session_type, skill_level: form.skill_level,
          skill_focus: form.skill_focus, venue: form.venue,
          title: form.title || (form.is_package
            ? `${form.session_count}-Session ${form.sport === "squash" ? "Squash" : "Pickleball"} Pack`
            : `${form.sport} ${form.session_type.replace("_"," ")}`),
          start, duration_min: form.is_package ? 0 : Number(form.duration_min),
          max_spaces: form.is_package ? 0 : Number(form.max_spaces),
          price_per_player: Number(form.price_per_player), notes: form.notes,
          save_as_template: !!form.save_as_template,
          is_assessment: !!form.is_assessment,
          shared_with_team: !!form.shared_with_team,
          region_ids: form.region_ids || [],
          reschedule_cutoff_hours: form.reschedule_cutoff_hours === "" || form.reschedule_cutoff_hours === null
            ? null
            : Math.max(0, Math.min(720, Number(form.reschedule_cutoff_hours))),
          // Iter143 — min-players safety net
          min_players: form.is_package || form.min_players === "" || form.min_players === null ? null : Number(form.min_players),
          min_deadline_at: form.is_package ? null : (form.min_deadline_at || null),
          // Iter94 — bundle knobs (ignored server-side unless is_package=true)
          is_package: !!form.is_package,
          session_count: form.is_package ? Number(form.session_count) : null,
          expiry_months: form.is_package ? Number(form.expiry_months) : null,
          accept_credits: form.is_package ? false : !!form.accept_credits,
          allow_pay_when_credit_available: form.is_package ? false : !!form.allow_pay_when_credit_available,
          allow_promo_codes: form.is_package ? !!form.allow_promo_codes : true,
        });
        toast.success(form.is_package
          ? "Package published to store"
          : form.is_assessment
            ? "Assessment slot published"
            : (form.save_as_template ? "Offering published & saved as template" : "Offering published"));
      }
      nav("/offerings");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider" data-testid="back-offerings">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>
      <div className="fade-up">
        <div className="label-mono mb-2">{isEdit ? "EDIT OFFERING" : "NEW OFFERING"}</div>
        <h1 className="h-display text-5xl tracking-tighter">{isEdit ? "Update the session." : "Publish a session."}</h1>
        <div className="text-sm text-[#A3A3A3] mt-1">
          {isEdit
            ? "Change venue, capacity, price or timing. Sport, session type and assessment flag are locked once players are enrolled."
            : "Set the venue, capacity and skill focus. Students see it instantly and self-enrol."}
        </div>
        {loadingEdit && (
          <div className="label-mono text-[#525252] mt-3">Loading offering…</div>
        )}
      </div>

      {locked && (
        <div className="card-surface p-5 border-l-4 border-[#FF9F1C] flex flex-wrap items-center justify-between gap-3" data-testid="offering-readonly-notice">
          <div className="text-sm"><span className="font-bold text-[#FF9F1C]">Read-only. </span>{locked}</div>
          <a href={`/offerings/new?copy_id=${editId}`} data-testid="offering-copy-as-new"
            className="min-h-[44px] px-4 py-2.5 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black inline-flex items-center gap-2">
            Copy as new session
          </a>
        </div>
      )}
      <form onSubmit={submit} className={`card-surface p-6 space-y-5 fade-up d1 ${locked ? "opacity-60" : ""}`} data-testid="offering-form">
        <fieldset disabled={!!locked} className="contents">
        {/* Iter94 — Single Session vs Package toggle. Immutable once the
            offering exists so an already-published bundle can't accidentally
            flip to a calendar slot (and vice-versa). Package mode collapses
            the date/time/capacity block and swaps it for a bundle configurator. */}
        {!isEdit && (
          <div>
            <div className="label-mono mb-2">Offering type</div>
            <div className="grid grid-cols-2 gap-2" data-testid="off-mode-picker">
              <button
                type="button"
                onClick={() => update("is_package", false)}
                data-testid="off-mode-single"
                className="px-3 py-2.5 text-xs uppercase tracking-wider border transition-colors"
                style={!form.is_package
                  ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 }
                  : { borderColor: "#262626", color: "#A3A3A3" }}
              >
                Single Session
              </button>
              <button
                type="button"
                onClick={() => update("is_package", true)}
                data-testid="off-mode-package"
                className="px-3 py-2.5 text-xs uppercase tracking-wider border transition-colors"
                style={form.is_package
                  ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 }
                  : { borderColor: "#262626", color: "#A3A3A3" }}
              >
                Package / Bundle
              </button>
            </div>
            {form.is_package && (
              <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-2">
                Packages are time-independent. Students buy them from the Store, and each credit unlocks any coach&apos;s credit-accepting session.
              </div>
            )}
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          <Picker label="Sport" value={form.sport} onChange={(v) => update("sport", v)} options={(() => {
            // Iter144 — Grey out sports the coach isn't assigned to. Admins
            // and users whose profile hasn't loaded yet see both. An empty
            // `mySports` array (admin) is treated as "no restriction".
            const restricted = Array.isArray(mySports) && mySports.length > 0;
            const disabledFor = (v) => restricted && !mySports.includes(v);
            return [
              { v: "squash", l: "Squash", c: "#CCFF00",
                disabled: disabledFor("squash"),
                disabledReason: "You're not assigned to Squash — ask an admin to add it to your coach profile." },
              { v: "pickleball", l: "Pickleball", c: "#FF4500",
                disabled: disabledFor("pickleball"),
                disabledReason: "You're not assigned to Pickleball — ask an admin to add it to your coach profile." },
            ];
          })()} testid="off-sport" />
          <Picker label="Session type" value={form.session_type} onChange={(v) => update("session_type", v)} options={[
            { v: "group", l: "Group", c: "#007AFF" }, { v: "performance", l: "Performance", c: "#FF4500" }, { v: "one_on_one", l: "1:1", c: "#CCFF00" }
          ]} testid="off-type" />
        </div>

        <Picker label="Skill level" value={form.skill_level} onChange={(v) => update("skill_level", v)}
          options={SKILL_LEVELS.map((l) => ({ v: l, l: l.charAt(0).toUpperCase() + l.slice(1), c: "#CCFF00" }))} testid="off-level" />

        <Text label="Title" value={form.title} onChange={(v) => update("title", v)} placeholder="e.g. Tuesday Squash Drills" testid="off-title" />
        <Text label={form.is_package ? "Venue (optional)" : "Venue"} value={form.venue} required={!form.is_package} onChange={(v) => update("venue", v)} placeholder={form.is_package ? "Leave blank — packages have no venue" : "e.g. Roko Gym Court 2"} testid="off-venue" />

        {/* Region picker — coach can tag the offering with one or more of
            their approved regions. Multi-tag is useful when a venue sits
            between two catchment areas (iter93). Empty regions list = coach
            hasn't set any yet; we surface a CTA. Iter93c adds "Templates" —
            personal shortcut chips for 2-3 region combos. */}
        <div>
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div className="label-mono">Regions <span className="text-[#525252] normal-case">· tap to toggle · you can pick more than one</span></div>
            {myRegions.length > 0 && (form.region_ids || []).length >= 2 && (
              <button
                type="button"
                onClick={saveTemplate}
                disabled={savingTemplate}
                data-testid="save-region-template"
                className="text-[10px] uppercase tracking-wider text-[#CCFF00] hover:opacity-80 inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Bookmark className="w-3 h-3" /> {savingTemplate ? "Saving…" : "Save as template"}
              </button>
            )}
          </div>
          {templates.length > 0 && (
            <div className="mb-3" data-testid="region-templates">
              <div className="label-mono text-[#525252] mb-1.5">TEMPLATES</div>
              <div className="flex flex-wrap gap-2">
                {templates.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center border border-[#262626] hover:border-[#CCFF00] transition-colors group"
                  >
                    <button
                      type="button"
                      onClick={() => applyTemplate(t)}
                      data-testid={`apply-template-${t.id}`}
                      className="px-3 py-1.5 text-xs text-white group-hover:text-[#CCFF00] flex items-center gap-1.5"
                      title={`Apply — ${t.region_ids.length} region${t.region_ids.length === 1 ? "" : "s"}`}
                    >
                      <Bookmark className="w-3 h-3" /> {t.name}
                      <span className="text-[9px] text-[#525252] ml-0.5">({t.region_ids.length})</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteTemplate(t)}
                      data-testid={`delete-template-${t.id}`}
                      className="border-l border-[#262626] px-1.5 py-1.5 text-[#525252] hover:text-[#FF4500]"
                      aria-label={`Delete template ${t.name}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {myRegions.length === 0 ? (
            <div className="p-3 border border-[#FF4500] text-sm text-[#FF4500]" data-testid="no-regions-warning">
              You don&apos;t have any approved regions yet. Visit your <a href="/profile" className="underline">profile</a> to pick where you coach. Until then this offering won&apos;t appear in any region filter.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="off-region-picker">
              {myRegions.map((r) => {
                const on = (form.region_ids || []).includes(r.id);
                return (
                  <button
                    type="button"
                    key={r.id}
                    onClick={() =>
                      update(
                        "region_ids",
                        on
                          ? (form.region_ids || []).filter((x) => x !== r.id)
                          : [...(form.region_ids || []), r.id],
                      )
                    }
                    data-testid={`off-region-${r.slug}`}
                    className="px-3 py-2 text-xs uppercase tracking-wider border transition-colors"
                    style={on
                      ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 }
                      : { borderColor: "#262626", color: "#A3A3A3" }}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Iter94 — Single-session block (hidden when in Package mode).
            Contains date/time/duration + capacity/price. Package mode has
            its own tighter block just below. */}
        {!form.is_package && (
          <>
            <div className="grid md:grid-cols-3 gap-4">
              <Text label="Date" type="date" value={form.date} onChange={(v) => update("date", v)} testid="off-date" />
              <Text label="Start time" type="time" value={form.time} onChange={(v) => update("time", v)} testid="off-time" />
              <Text label="Duration (min)" type="number" value={form.duration_min} onChange={(v) => update("duration_min", v)} testid="off-duration" />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Text label="Max spaces" type="number" value={form.max_spaces} onChange={(v) => update("max_spaces", v)} testid="off-spaces" />
              <Text label="Price per player (£)" type="number" value={form.price_per_player} onChange={(v) => update("price_per_player", v)} testid="off-price" />
            </div>

            {/* Iter143 — Min-players safety net. Coach sets a floor and the
                cut-off. If the deadline passes with fewer than min_players
                enrolled, they get a bell notification. Enrolled players see
                a "Rally the Court" button while the floor isn't met. */}
            <div className="grid md:grid-cols-2 gap-4">
              <Text
                label="Minimum players (optional)"
                type="number"
                value={form.min_players}
                onChange={(v) => update("min_players", v)}
                testid="off-min-players"
              />
              <Text
                label="Rally cut-off (optional)"
                type="datetime-local"
                value={form.min_deadline_at}
                onChange={(v) => update("min_deadline_at", v)}
                testid="off-min-deadline"
              />
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#525252] -mt-2">
              If the minimum isn&apos;t met by the cut-off we&apos;ll ping you so you can decide to run the session or cancel. Booked players will see a &quot;Rally the Court&quot; button in the meantime.
            </div>

            {/* Iter94 — Accept-credits toggle. Only surfaced on single-session
                offerings. When ON, students with an active credit balance see
                a "[Use 1 Credit]" button instead of Pay. */}
            <label className="flex items-start gap-3 cursor-pointer group" data-testid="off-accept-credits-wrap">
              <input
                type="checkbox"
                checked={!!form.accept_credits}
                onChange={(e) => update("accept_credits", e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#CCFF00]"
                data-testid="off-accept-credits"
              />
              <div>
                <div className="text-sm text-white">Accept Package Credits</div>
                <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-0.5">
                  Students who own a Rally Lab package can spend one credit here instead of paying the £{form.price_per_player} price.
                </div>
              </div>
            </label>

            {/* Iter112 — Player choice: credit vs cash. Only surfaced when
                Accept Credits is on. Default ON so students can save their
                credit for later (nicer UX). Off = credit-only, legacy behaviour. */}
            {form.accept_credits && (
              <label className="flex items-start gap-3 cursor-pointer pl-7" data-testid="off-allow-pay-wrap">
                <input
                  type="checkbox"
                  checked={!!form.allow_pay_when_credit_available}
                  onChange={(e) => update("allow_pay_when_credit_available", e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#CCFF00]"
                  data-testid="off-allow-pay"
                />
                <div>
                  <div className="text-sm text-white">Let the student choose &mdash; pay OR use a credit</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-0.5">
                    {form.allow_pay_when_credit_available
                      ? `They'll see "Use 1 Credit" with a small "Pay £${form.price_per_player} instead →" fallback link.`
                      : "Credit-only — students with a balance MUST use a credit here."}
                  </div>
                </div>
              </label>
            )}
          </>
        )}

        {/* Iter94 — Package configurator. Session count + expiry window +
            bundle price. Coach can type any integer 1-36 for a bespoke
            expiry (user choice 1b — 3/6/12 pill + free-form fallback). */}
        {form.is_package && (
          <div className="border border-dashed border-[#CCFF00] p-4 space-y-4" data-testid="off-package-config">
            <div className="grid md:grid-cols-3 gap-4">
              <Text label="Sessions in pack" type="number" value={form.session_count} onChange={(v) => update("session_count", Math.max(1, Math.min(100, Number(v || 1))))} testid="off-session-count" />
              <div>
                <div className="label-mono mb-2">Expiry (months)</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {[3, 6, 12].map((m) => (
                    <button
                      type="button"
                      key={m}
                      onClick={() => update("expiry_months", m)}
                      data-testid={`off-expiry-${m}`}
                      className="px-2.5 py-1.5 text-xs border transition-colors"
                      style={Number(form.expiry_months) === m
                        ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 }
                        : { borderColor: "#262626", color: "#A3A3A3" }}
                    >
                      {m}m
                    </button>
                  ))}
                  <input
                    type="number"
                    value={form.expiry_months}
                    min={1}
                    max={36}
                    onChange={(e) => update("expiry_months", Math.max(1, Math.min(36, Number(e.target.value || 1))))}
                    className="w-16 bg-[#0A0A0A] border border-[#262626] px-2 py-1.5 text-xs text-white focus:border-[#CCFF00] focus:outline-none"
                    data-testid="off-expiry-custom"
                  />
                </div>
              </div>
              <Text label="Bundle price (£)" type="number" value={form.price_per_player} onChange={(v) => update("price_per_player", v)} testid="off-price" />
            </div>
            <div className="text-[11px] text-[#A3A3A3]">
              Students who buy this pack get <strong className="text-white">{form.session_count || 0}</strong> credit{Number(form.session_count) === 1 ? "" : "s"} valid for <strong className="text-white">{form.expiry_months || 0}</strong> month{Number(form.expiry_months) === 1 ? "" : "s"}. Credits can be spent on any session tagged &ldquo;Accept Package Credits&rdquo;.
            </div>
            {/* Iter97/98 — per-offering promo lock. Copy tuned in iter98b to
                clarify two things students often ask about: (1) codes NEVER
                stack (single code slot at checkout) and (2) an active code
                only applies to this offering if BOTH sides agree — the coach
                ticks the box here AND the admin lists this offering on the
                code's allowlist (or leaves the allowlist empty for universal
                codes). Ticking the box below is necessary but not sufficient. */}
            <label className="flex items-start gap-2 text-xs cursor-pointer p-3 border border-[#262626] hover:border-[#CCFF00] transition-colors" data-testid="off-allow-promo-label">
              <input
                type="checkbox"
                data-testid="off-allow-promo"
                checked={!!form.allow_promo_codes}
                onChange={(e) => update("allow_promo_codes", e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <div className="font-semibold text-white">Allow discount codes on this package</div>
                <div className="text-[10px] text-[#A3A3A3] mt-0.5 leading-relaxed">
                  {form.allow_promo_codes ? (
                    <>
                      Only <span className="text-white">one code per checkout</span> — codes never stack.
                      A code will apply here only if an admin has either left its allowlist blank
                      (universal) or added this package to it. Ring-fence a specific package with a
                      code by naming it in <span className="text-white">Admin → Promo codes → Restrict to packages</span>.
                    </>
                  ) : (
                    <>This package is ring-fenced — <span className="text-white">no promo code</span> will apply, even active ones.</>
                  )}
                </div>
              </div>
            </label>
          </div>
        )}

        <div>
          <div className="label-mono mb-2">Skill focus tags ({form.sport})</div>
          <div className="flex flex-wrap gap-2">
            {skills.map((s) => {
              const on = form.skill_focus.includes(s);
              return (
                <button key={s} type="button" onClick={() => toggleSkill(s)} data-testid={`focus-${s.replace(/\s+/g,"")}`}
                  className="px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors"
                  style={on ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 } : { borderColor: "#262626", color: "#A3A3A3" }}>
                  {s}
                </button>
              );
            })}
          </div>
        </div>

        <Text label="Notes (optional)" textarea value={form.notes} onChange={(v) => update("notes", v)} testid="off-notes" />

        {/* Optional per-offering reschedule/cancel cutoff override. Leave blank
            to fall back to the coach's global setting (48h default). Useful
            when a specific session — e.g. a paid pro clinic — needs a longer
            or shorter cancel window than the coach's usual rule. */}
        <div data-testid="off-cutoff-wrap">
          <div className="label-mono mb-2">Reschedule / cancel cutoff (optional)</div>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="number"
              min="0"
              max="720"
              step="1"
              value={form.reschedule_cutoff_hours}
              onChange={(e) => update("reschedule_cutoff_hours", e.target.value)}
              placeholder="Use coach default"
              data-testid="off-cutoff-hours"
              className="w-40 bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]"
            />
            <span className="text-xs text-[#A3A3A3]">hours before start</span>
          </div>
          <div className="text-[11px] text-[#525252] mt-1.5 leading-relaxed">
            Leave blank to use your global setting on the dashboard. Only override for sessions that need a tighter or looser window than the default.
          </div>
        </div>

        {/* Mark this session as the mandatory Initial Assessment slot */}
        <label
          className="flex items-start gap-3 cursor-pointer p-3 border transition-colors"
          style={{ borderColor: form.is_assessment ? "#FF4500" : "#262626", background: form.is_assessment ? "rgba(255,69,0,0.06)" : "transparent" }}
          data-testid="off-is-assessment-wrap"
        >
          <input
            type="checkbox"
            checked={!!form.is_assessment}
            onChange={(e) => {
              const v = e.target.checked;
              update("is_assessment", v);
              if (v) {
                // Sensible assessment defaults: 1:1, 1 space, all-levels
                update("session_type", "one_on_one");
                update("max_spaces", 1);
                update("skill_level", "all");
                if (!form.title) update("title", "Initial Pickleball Assessment");
                if (!form.price_per_player || Number(form.price_per_player) < 20) update("price_per_player", 20);
              }
            }}
            data-testid="off-is-assessment"
            className="mt-0.5 w-4 h-4 accent-[#FF4500]"
          />
          <div className="flex-1">
            <div className="text-sm font-semibold flex items-center gap-2">
              <span style={{ color: form.is_assessment ? "#FF4500" : "#E5E5E5" }}>Mandatory Initial Assessment session</span>
            </div>
            <div className="text-xs text-[#A3A3A3] mt-0.5 leading-relaxed">
              Tick this to mark the session as a player&apos;s on-court assessment. New players must book one of these <em>once</em> before Performance &amp; 1:1 sessions unlock. When the student turns up, just open Today&apos;s Plan and tick &ldquo;Mark attended&rdquo; — that unlocks their full booking access automatically. Defaults to 1:1 · 1 spot · all levels — bump <em>Max spaces</em> above if you want to run a small-group assessment day. Set the price to whatever you charge (or £0 if you bundle the assessment into a coaching package).
            </div>
          </div>
        </label>

        {/* Share-with-team — only renders when the coach belongs to at least one team.
            Toggling on triggers a live team-wide conflict probe (see effect above) so
            the coach sees any teammate clash BEFORE publishing. */}
        {myTeams.length > 0 && (
          <label
            className="flex items-start gap-3 cursor-pointer p-3 border transition-colors"
            style={{ borderColor: form.shared_with_team ? "#007AFF" : "#262626", background: form.shared_with_team ? "rgba(0,122,255,0.06)" : "transparent" }}
            data-testid="off-share-team-wrap"
          >
            <input
              type="checkbox"
              checked={!!form.shared_with_team}
              onChange={(e) => update("shared_with_team", e.target.checked)}
              data-testid="off-share-team"
              className="mt-0.5 w-4 h-4 accent-[#007AFF]"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold">Share with {myTeams.length === 1 ? `team "${myTeams[0].name}"` : `my ${myTeams.length} teams`}</div>
              <div className="text-xs text-[#A3A3A3] mt-0.5 leading-relaxed">
                This session will surface on every team-mate&apos;s calendar and count toward their busy / conflict checks. Use this for shared club drills, joint training nights, or any session where another coach might need to know not to double-book the same court.
              </div>
              {form.shared_with_team && teamConflict && (teamConflict.hard?.length > 0 || teamConflict.soft?.length > 0) && (
                <div className="mt-3 p-3 text-xs border" data-testid="team-conflict-banner"
                     style={{ borderColor: teamConflict.hard?.length ? "#FF4500" : "#CCFF00", background: teamConflict.hard?.length ? "rgba(255,69,0,0.08)" : "rgba(204,255,0,0.06)" }}>
                  <div className="font-semibold mb-1" style={{ color: teamConflict.hard?.length ? "#FF4500" : "#CCFF00" }}>
                    {teamConflict.hard?.length ? `Team conflict — ${teamConflict.hard.length} clash${teamConflict.hard.length === 1 ? "" : "es"}` : `Tight gap — ${teamConflict.soft.length} back-to-back`}
                  </div>
                  {(teamConflict.hard || []).slice(0, 3).map((c, i) => (
                    <div key={`h-${i}`} className="text-[#FFA88A]">· {c.reason}</div>
                  ))}
                  {(teamConflict.soft || []).slice(0, 2).map((c, i) => (
                    <div key={`s-${i}`} className="text-[#CCFF00]">· {c.reason}</div>
                  ))}
                </div>
              )}
            </div>
          </label>
        )}

        {/* Save-as-template — adds this offering to the coach's reusable profile templates */}
        {!isEdit && (
          <label
            className="flex items-start gap-3 cursor-pointer p-3 border border-[#262626] hover:border-[#CCFF00] transition-colors"
            data-testid="off-save-as-template-wrap"
          >
            <input
              type="checkbox"
              checked={!!form.save_as_template}
              onChange={(e) => update("save_as_template", e.target.checked)}
              data-testid="off-save-as-template"
              className="mt-0.5 w-4 h-4 accent-[#CCFF00]"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold">Save as a template in my profile</div>
              <div className="text-xs text-[#A3A3A3] mt-0.5">
                Tick this and we&apos;ll add this session to your reusable templates. Next time you publish a similar session, just pick it from your profile and tweak the date — no need to re-enter venue, price, or skill focus.
              </div>
            </div>
          </label>
        )}

        <button type="submit" disabled={saving || loadingEdit} className="btn-volt w-full py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="off-submit">
          <Save className="w-4 h-4" /> {saving ? (isEdit ? "Saving…" : "Publishing…") : (isEdit ? "Save changes" : "Publish offering")}
        </button>
        </fieldset>
      </form>
    </div>
  );
}

function Picker({ label, value, onChange, options, testid }) {
  return (
    <div>
      <div className="label-mono mb-2">{label}</div>
      <div className="grid grid-cols-3 gap-2" data-testid={testid}>
        {options.map((o) => {
          const on = value === o.v;
          const isDisabled = !!o.disabled;
          return (
            <button
              key={o.v}
              type="button"
              disabled={isDisabled}
              title={isDisabled ? (o.disabledReason || "Not available") : undefined}
              onClick={() => { if (!isDisabled) onChange(o.v); }}
              data-testid={testid ? `${testid}-opt-${o.v}` : undefined}
              className="py-2.5 text-xs uppercase tracking-wider border transition-colors disabled:cursor-not-allowed"
              style={
                isDisabled
                  ? { borderColor: "#1F1F1F", color: "#3F3F3F", background: "#0F0F0F", opacity: 0.55 }
                  : on
                    ? { background: o.c, color: "#0A0A0A", borderColor: o.c, fontWeight: 700 }
                    : { borderColor: "#262626", color: "#A3A3A3" }
              }
            >
              {o.l}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Text({ label, value, onChange, type = "text", placeholder, textarea, required, testid }) {
  return (
    <div>
      <div className="label-mono mb-2">{label}{required && <span className="text-[#FF4500]"> *</span>}</div>
      {textarea ? (
        <textarea value={value || ""} rows={2} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
          className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
      ) : (
        <input type={type} value={value ?? ""} required={required} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
          className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
      )}
    </div>
  );
}
